import { useEffect, useRef, useState } from 'react';
import { colors, createNote } from '../util/notes.mjs';
import { loadNotes, loadBoardName } from '../util/storage.js';
import { createHistory } from '../util/history.mjs';
import useAutosave from './useAutosave.js';

// Own the board's note collection, selection, stacking order, and history.
const useNoteBoard = function(boardRef, boardId) {
  const topLayer = useRef(0);
  const storageAvailable = useRef(false);

  const [notes, renderNotes] = useState([]);
  const notesRef = useRef([]);
  const history = useRef(createHistory());
  const setNotes = function(update, group, record = true) {
    const current = notesRef.current;
    const next = typeof update === 'function' ? update(current) : update;
    if (next === current) return;
    if (record) history.current.record(current, group);
    notesRef.current = next;
    renderNotes(next);
  };
  const restoreHistory = function(direction) {
    setNotes(history.current[direction](notesRef.current), undefined, false);
    setSelected(null);
    setDeletedNote(null);
  };
  const undo = () => restoreHistory('undo');
  const redo = () => restoreHistory('redo');
  const [boardName, setBoardName] = useState('my board');
  const [ready, setReady] = useState(false);
  const [selected, setSelected] = useState(null);

  const nextColor = useRef(0);

  const [message, setMessage] = useState('');
  const [deletedNote, setDeletedNote] = useState(null);

  useEffect(() => {
    let cancelled = false;

    Promise.all([loadNotes(boardId), loadBoardName(boardId)]).then(([saved, name]) => {
      if (cancelled) return;

      // A saved empty board stays empty; only a first visit gets a welcome note.
      const initial = saved ?? [createNote({
        width: Math.min(300, window.innerWidth - 64),
        text: 'A space for your thoughts.\n\nWrite something, paste an image, or collect an idea.\n\nDrag the top to move me. Pull the corner to make more room.'
      })];

      // Continue stacking above the saved notes after a reload.
      topLayer.current = Math.max(0, ...initial.map(note => note.layer || 0));
      storageAvailable.current = true;

      const lastNote = initial.filter(note => note.kind !== 'image').at(-1);

      nextColor.current = lastNote ? (colors.findIndex(color => color.id === lastNote.color) + 1) % colors.length : 0;
      setNotes(initial, undefined, false);
      setBoardName(name);
      setReady(true);
    }).catch(() => {
      if (cancelled) return;

      setMessage('Local storage is unavailable. You can use this board, but changes will not survive a reload.');
      setReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const saveStatus = useAutosave({
    notes,
    boardId,
    boardName,
    ready,
    storageAvailable: storageAvailable.current,
    onError: setMessage
  });

  const updateNote = function(id, patch, group) {
    setNotes(current => {
      const target = current.find(note => note.id === id);
      if (!target || Object.keys(patch).every(key => target[key] === patch[key])) return current;
      return current.map(note => note.id === id ? {...note, ...patch} : note);
    }, group || `${id}:${Object.keys(patch).sort().join(',')}`);
  };

  const selectNote = function(id) {
    setSelected(id);
    setNotes(current => {
      const target = current.find(note => note.id === id);

      if (!target || target.layer === topLayer.current) return current;

      const layer = ++topLayer.current;

      return current.map(note => note.id === id ? {
        ...note,
        layer
      } : note);
    }, undefined, false);
  };

  const addNote = function(content = {}, position) {
    if (!ready) return;

    const board = boardRef.current;

    // Stagger new notes within the visible part of the board.
    const zoom = Number(board.dataset.zoom) || 1;
    const viewportWidth = board.clientWidth / zoom;
    const offset = notes.length % 6 * 24;
    const width = Math.max(240, Math.min(300, viewportWidth - 48));

    const note = {
      ...createNote({
        color: colors[nextColor.current].id,
        width,
        x: (-(Number(board.dataset.panX) || 0)) / zoom + Math.max(16, Math.min(32 + offset, viewportWidth - width - 16)),
        y: (-(Number(board.dataset.panY) || 0)) / zoom + 32 + offset,
        ...position,
        ...content
      }),
      ...content,
      layer: ++topLayer.current
    };

    if (board.dataset.snap === 'true') {
      note.x = Math.round(note.x / 20) * 20;
      note.y = Math.round(note.y / 20) * 20;
    }

    if (note.kind !== 'image') nextColor.current = (nextColor.current + 1) % colors.length;

    setNotes(current => [...current, note]);
    setSelected(note.id);

    // Wait for React to mount the textarea before focusing it.
    requestAnimationFrame(() => {
      board.querySelector(`[data-note-id="${note.id}"] ${note.kind === 'image' ? '.boardImageDrag' : 'textarea'}`)?.focus({
        preventScroll: true
      });
    });
  };

  const appendContent = function(id, content, position) {
    if (!content.text && !content.images.length) {
      setMessage('Copy some text or an image first, then paste it here.');

      return;
    }

    if (!id) {
      if (content.images.length) {
        content.images.forEach((image, index) => addNote({
          kind: 'image',
          images: [image]
        }, position && {
          x: position.x + index * 24,
          y: position.y + index * 24
        }));

        if (content.text) addNote({
          text: content.text
        }, position);
      } else {
        addNote(content, position);
      }

      return;
    }

    setNotes(current => current.map(note => {
      if (note.id !== id) return note;

      return {
        ...note,
        text: content.text ? [note.text, content.text].filter(Boolean).join('\n') : note.text,
        images: [...note.images, ...content.images]
      };
    }));
  };

  const deleteNote = function(id) {
    setDeletedNote(notes.find(note => note.id === id));
    setNotes(current => current.filter(note => note.id !== id));

    if (selected === id) setSelected(null);
  };

  const duplicateNote = function(id) {
    const source = notes.find(note => note.id === id);

    if (!source) return;

    const copy = {
      ...source,
      id: crypto.randomUUID(),
      x: source.x + 20,
      y: source.y + 20,
      layer: ++topLayer.current
    };

    setNotes(current => [...current, copy]);
    setSelected(copy.id);
  };

  const undoDelete = function() {
    if (!deletedNote) return;

    setNotes(current => [...current, {
      ...deletedNote,
      layer: ++topLayer.current
    }]);
    setDeletedNote(null);
  };

  const dismissMessage = function() {
    setMessage('');
    setDeletedNote(null);
  };

  return {
    boardName,
    setBoardName,
    notes,
    ready,
    selected,
    setSelected,
    saveStatus,
    message,
    setMessage,
    deletedNote,
    updateNote,
    selectNote,
    addNote,
    appendContent,
    deleteNote,
    duplicateNote,
    undo,
    redo,
    undoDelete,
    dismissMessage
  };
};

export default useNoteBoard;
